(<template>
  <div class="assignment-date">
    <div v-for="(job, index) in jobSeriesList"
         :key="index"
         class="assignment-date__cont">
      <sk-select :description="langText"
                 :items-list="languages"
                 :preselected-value="job.languageToId"
                 :default-value="langDefaultValue"
                 :validation-name="job.languageToIdValidation"
                 class="post-form__field assignment-date__language"
                 @csvaluechanged="job.catchLanguage" />
      <sk-datepicker :preselected-value="job.date"
                     :default-value="'--/--/----'"
                     :full-calendar="allowPastBooking"
                     :prevent-call="true"
                     :blocked-dates-array="blockedStartDatesArray"
                     class="post-form__field assignment-date__datepicker"
                     @changeday="job.catchDate" />
      <sk-timepicker :preselected-value="job.startTime"
                     :start-select-from="allowPastBooking ? '' : startTimeBeginning(job.date)"
                     :auto-scroll="true"
                     :default-value="'--:--'"
                     :last-item="false"
                     :mob-numeric-keyboard="true"
                     :description="startTimeText"
                     :validation-name="job.startTimeValidation"
                     :time-step="$timepickerStep()"
                     class="post-form__field assignment-date__timepicker"
                     @timepickervaluechanged="job.catchStartTime" />
      <sk-timepicker :preselected-value="job.finishTime"
                     :auto-scroll="true"
                     :default-value="'--:--'"
                     :last-item="true"
                     :mob-numeric-keyboard="true"
                     :description="finishTimeText"
                     :validation-name="job.finishTimeValidation"
                     :time-step="$timepickerStep()"
                     class="post-form__field assignment-date__timepicker"
                     @timepickervaluechanged="job.catchFinishTime" />
      <button v-if="index >= 2"
              class="sk-btn post-form__field assignment-date__btn--delete"
              type="button"
              @click="deleteDateTime(index)"></button>
    </div>
    <slot name="errors"></slot>
    <button class="sk-btn sk-btn--white post-form__field assignment-date__btn assignment-date__btn--add"
            type="button"
            @click="addAssignment">{{ $gettext('Add assignment') }}</button>
  </div>
</template>)

<script>
  import {mapActions, mapMutations, mapGetters, mapState} from 'vuex';
  import helpers from '@/helpers';

  export default {
    computed: {
      ...mapGetters('PostingStore/DateTimeStore', ['minJobDuration']),
      ...mapGetters('UserInfoStore', ['urgentBookingsBlocked']),
      ...mapState('PostingStore/SeriesAssignmentStore', {
        jobSeries: ({jobSeries}) => jobSeries,
        errors: ({errors}) => errors
      }),
      jobSeriesList() {
        return this.jobSeries.map((job, index) => {
          return {
            date: job.startTime ? this.$moment(job.startTime).format('YYYY-MM-DD') : '',
            startTime: job.startTime ? this.$moment(job.startTime).format('HH:mm') : '',
            finishTime: job.finishTime ? this.$moment(job.finishTime).format('HH:mm') : '',
            languageToId: job.languageToId || '',
            languageToIdValidation: this.errors[`languageToId${index}`]?.name,
            startTimeValidation: this.errors[`startTime${index}`]?.name,
            finishTimeValidation: this.errors[`finishTime${index}`]?.name,
            catchLanguage: (value) => { this.catchLanguage(index, value); },
            catchDate: (value) => { this.catchDate(index, value); },
            catchStartTime: (value) => { this.catchStartTime(index, value); },
            catchFinishTime: (value) => { this.catchFinishTime(index, value); }
          };
        });
      },
      languages() { return this.$store.getters.languagesForSelect || []; },
      langDefaultValue() { return this.$gettext('Select a language'); },
      langText() { return this.$gettext('Language'); },
      allowPastBooking() { return this.$isGodmode(); },
      // from about 23:25 the earliest start time is tomorrow, so nothing is left to book today
      blockedStartDatesArray() {
        if (!this.urgentBookingsBlocked) return [];
        const today = this.$moment().format('YYYY-MM-DD');
        return helpers.dateTime.earliestNonUrgentStartTime(this.$moment).format('YYYY-MM-DD') === today ? [] : [today];
      },
      startTimeText() { return this.$gettext('Start time'); },
      finishTimeText() { return this.$gettext('Finish time'); },
      showDeleteButton() { return this.jobSeries.length > 1; }
    },
    methods: {
      ...mapActions('PostingStore/SeriesAssignmentStore', [
        'setStartTime',
        'setFinishTime',
        'addAssignment',
        'deleteDateTime',
        'setDate'
      ]),
      ...mapMutations('PostingStore/SeriesAssignmentStore', [
        'setLanguage'
      ]),
      catchLanguage(index, value) { this.setLanguage({index, value}); },
      catchDate(index, value) { this.setDate({index, value}); },
      catchStartTime(index, value) { this.setStartTime({index, value}); },
      catchFinishTime(index, value) { this.setFinishTime({index, value}); },
      startTimeBeginning(date) {
        if (this.urgentBookingsBlocked) {
          const earliestStartTime = helpers.dateTime.earliestNonUrgentStartTime(this.$moment);
          return date === earliestStartTime.format('YYYY-MM-DD') ? earliestStartTime.format('HH:mm') : '';
        }
        return date === this.$moment().format('YYYY-MM-DD') ? helpers.dateTime.convertCurrentTime(this.$moment) : '';
      },
      finishTimeBeginning(startTime) {
        if (startTime) {
          const time = this.$moment(startTime, 'HH:mm').add(this.minJobDuration, 'minutes');
          if (time.hours() == 0 && time.minutes() == 0) { return '23:59'; }
          return time.format('HH:mm');
        }
        return '';
      }
    }
  };
</script>

<style>
  .assignment-date .sk-time-picker__description {
    height: 22px;
  }

  @media (max-width: 767px) {
    .assignment-date .sk-time-picker__description {
      height: 20px;
    }
  }
</style>

<style scoped src="@assets/css/assignment_dates.css"></style>
